import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { Building2, Plus, MapPin, Clock, Users, ChevronRight, Search, Trash2 } from 'lucide-react';
import { LayoutWrapper } from '@/components/LayoutWrapper';
import { egonLocations, type EgonLocation } from '@/data/egonLocations';
import { useNavigate } from 'react-router-dom';

interface Unit {
  id: string;
  name: string;
  address: string | null;
  timezone: string;
  created_at: string;
  tenant_id: string;
}

const Units = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    timezone: 'UTC'
  });
  const [locationType, setLocationType] = useState<'egon' | 'custom'>('custom');
  const [selectedEgonLocation, setSelectedEgonLocation] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user) {
      fetchUnits();
    }
  }, [user]);

  const fetchUnits = async () => {
    try {
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user?.id)
        .single();

      if (!userRole) return;

      const { data, error } = await supabase
        .from('units')
        .select('*')
        .eq('tenant_id', userRole.tenant_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUnits(data || []);
    } catch (error: any) {
      console.error('Error fetching units:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleEgonLocationSelect = (locationId: string) => {
    const location = egonLocations.find(loc => loc.id === locationId);
    if (location) {
      setSelectedEgonLocation(locationId);
      setFormData(prev => ({
        ...prev,
        name: `Egon ${location.name}`,
        address: location.fullAddress
      }));
    }
  };

  const handleLocationTypeChange = (type: 'egon' | 'custom') => {
    setLocationType(type);
    if (type === 'custom') {
      setSelectedEgonLocation('');
      setFormData(prev => ({
        ...prev,
        name: '',
        address: ''
      }));
    }
  };

  const filteredEgonLocations = egonLocations.filter(location =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const createUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setCreating(true);
    try {
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .single();

      if (!userRole) throw new Error('User not associated with any organization');

      const { error } = await supabase
        .from('units')
        .insert({
          name: formData.name,
          address: formData.address || null,
          timezone: formData.timezone,
          tenant_id: userRole.tenant_id
        });

      if (error) throw error;

      toast({
        title: "Unit created successfully",
        description: `${formData.name} has been added to your organization.`,
      });

      setDialogOpen(false);
      setFormData({ name: '', address: '', timezone: 'UTC' });
      setLocationType('custom');
      setSelectedEgonLocation('');
      setSearchQuery('');
      fetchUnits();
    } catch (error: any) {
      toast({
        title: "Error creating unit",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const deleteUnit = async (unitId: string, unitName: string) => {
    setDeleting(unitId);
    try {
      const { error } = await supabase
        .from('units')
        .delete()
        .eq('id', unitId);

      if (error) throw error;

      toast({
        title: "Unit deleted successfully",
        description: `${unitName} has been removed from your organization.`,
      });

      fetchUnits();
    } catch (error: any) {
      toast({
        title: "Error deleting unit",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <LayoutWrapper>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading units...</p>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Units Management</h1>
            <p className="text-sm text-muted-foreground">Manage your restaurant locations and facilities</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add New Unit
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create New Unit</DialogTitle>
                <DialogDescription>
                  Add a new location to your organization for food safety management.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={createUnit}>
                <div className="space-y-4 py-4">
                  <div className="space-y-3">
                    <Label>Location Type</Label>
                    <RadioGroup 
                      value={locationType} 
                      onValueChange={(value: 'egon' | 'custom') => handleLocationTypeChange(value)}
                      className="flex gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="egon" id="egon" />
                        <Label htmlFor="egon">Egon Restaurant</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="custom" id="custom" />
                        <Label htmlFor="custom">Custom Location</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {locationType === 'egon' ? (
                    <div className="space-y-2">
                      <Label htmlFor="egon-location">Select Egon Location *</Label>
                      <div className="space-y-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 z-10" />
                          <Input
                            placeholder="Type to search: Oslo, Bergen, Byporten..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        {searchQuery && (
                          <p className="text-xs text-muted-foreground">
                            Found {filteredEgonLocations.length} location{filteredEgonLocations.length !== 1 ? 's' : ''}
                          </p>
                        )}
                        <Select value={selectedEgonLocation} onValueChange={handleEgonLocationSelect}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose an Egon restaurant" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {filteredEgonLocations.length > 0 ? (
                              filteredEgonLocations.map((location) => (
                                <SelectItem key={location.id} value={location.id}>
                                  <div className="flex flex-col">
                                    <span className="font-medium">Egon {location.name}</span>
                                    <span className="text-sm text-muted-foreground">{location.fullAddress}</span>
                                  </div>
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="no-results" disabled>
                                {searchQuery ? `No locations matching "${searchQuery}"` : 'No locations found'}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="name">Unit Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="e.g., Kitchen A, Main Restaurant"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address">Address</Label>
                        <Input
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Street address (optional)"
                        />
                      </div>
                    </>
                  )}

                  {locationType === 'egon' && selectedEgonLocation && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="name">Unit Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="e.g., Kitchen A, Main Restaurant"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address">Address</Label>
                        <Input
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Street address"
                        />
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <select
                      id="timezone"
                      name="timezone"
                      value={formData.timezone}
                      onChange={(e) => handleInputChange(e as any)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                    </select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={creating || (locationType === 'egon' && !selectedEgonLocation)}
                  >
                    {creating ? 'Creating...' : 'Create Unit'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {units.length === 0 ? (
          <Card className="glass-panel text-center py-12">
            <CardContent>
              <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">No Units Yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first unit to start managing food safety operations.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {units.map((unit) => (
              <Card 
                key={unit.id} 
                className="glass-panel hover:glass-panel-elevated transition-all duration-300 group"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div 
                      className="flex items-center space-x-3 cursor-pointer flex-1"
                      onClick={() => navigate(`/checklists?unit=${unit.id}`)}
                    >
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{unit.name}</CardTitle>
                        <CardDescription className="flex items-center mt-1">
                          <MapPin className="h-3 w-3 mr-1" />
                          {unit.address || 'No address set'}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div 
                        className="cursor-pointer"
                        onClick={() => navigate(`/checklists?unit=${unit.id}`)}
                      >
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            disabled={deleting === unit.id}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Unit</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{unit.name}"? This action cannot be undone and will remove all associated checklists and data.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteUnit(unit.id, unit.name)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {deleting === unit.id ? 'Deleting...' : 'Delete'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-muted-foreground">
                      <Clock className="h-3 w-3 mr-1" />
                      {unit.timezone}
                    </div>
                    <div className="flex items-center text-muted-foreground">
                      <Users className="h-3 w-3 mr-1" />
                      Active
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default Units;